/** @file scopeBandsOf: an impostor's entries narrowed to a creation scope's head pitch bands (CR-002). */
import { entryFor, type EntryChoice } from '@zilo/pose3d-facet/entries/entryFor';
import { PITCH_RANGES } from '@zilo/pose3d-format/tables/pitchRanges';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { TemplateScope } from '../types/puppetInputs';

const LEVEL = PITCH_RANGES.NORM[0];
/** The bands each narrowing pitch scope serves: the level band; level, one up (UP1) and one down (DOWN1). */
const BANDS: Readonly<Record<'none' | 'basic', readonly number[]>> = {
  none: [LEVEL],
  basic: [PITCH_RANGES.DOWN1[0], LEVEL, PITCH_RANGES.UP1[0]],
};

/** Entries that differ only in their bands share a group: same frame keys and variant flags. */
const groupOf = (e: FacetEntry): string => JSON.stringify([[...e.frames].sort((a, b) => a - b), [...e.variants].sort()]);

/**
 * The entries a pitch scope keeps, in order: `full` keeps all; `none` and `basic` keep, per group of entries that
 * differ only in bands, the entry the lookup (`entryFor`, nearest-range fallback included) shows at each band they
 * serve; `none` then serves its kept entries at every band (`['ALL']`). Kept entries are the caller's, edited in place.
 */
export function scopeBandsOf(entries: readonly FacetEntry[], pitch: NonNullable<TemplateScope['pitch']>): FacetEntry[] {
  if (pitch === 'full') return [...entries];
  const kept = new Set<FacetEntry>();
  const out: EntryChoice = { entry: -1, frame: 0, band: -1, folded: false, hidden: false };
  for (const key of new Set(entries.map(groupOf))) {
    const group = entries.filter((e) => groupOf(e) === key);
    const first = group[0];
    if (first === undefined) continue;
    for (const band of BANDS[pitch]) {
      entryFor(group, first.frames[0] ?? 0, band, false, new Set(first.variants), 'hide', out);
      const pick = group[out.entry];
      if (pick !== undefined) kept.add(pick);
    }
  }
  const result = entries.filter((e) => kept.has(e));
  if (pitch === 'none') for (const e of result) e.bands = ['ALL'];
  return result;
}
